import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Clock } from 'lucide-react';

import { usePermissions } from '@/hooks/use-permissions';
import { PERMISSION } from '@/features/users-roles/constants';
import { ROUTES } from '@/router/constant/routes';

import { useBillingSubscription } from '../hooks/use-billing-subscription';
import { isTrialExpiredSubscription } from '../utils/subscription-state';
import { trialDaysLeft } from '../utils/trial';

/**
 * Always-visible trial countdown in the app header (Subscription & Billing
 * Upgrade §6/Phase C): "Trial · N days left", linking to Plan & Billing.
 * Card-free trials mean there is nothing to charge at expiry — this chip is
 * how the deadline stays legible without nagging. Rendered only for
 * billing-capable members (the link's target is theirs to act on) and only
 * while the subscription is TRIALING.
 */
export function TrialHeaderChip() {
    const { t } = useTranslation();
    const { hasPermission } = usePermissions();
    const canReadBilling = hasPermission(PERMISSION.BILLING_READ);
    const { data } = useBillingSubscription({ enabled: canReadBilling });

    const subscription = data?.subscription;
    // Expired covers both the lazy TRIALING-past-deadline snapshot and the
    // SA's LOCKED flip — the chip must not vanish at the exact moment it
    // matters most.
    const expired = canReadBilling && isTrialExpiredSubscription(data);
    if (!canReadBilling || (subscription?.status !== 'TRIALING' && !expired)) return null;
    const daysLeft = expired ? 0 : trialDaysLeft(subscription?.trialEndsAt ?? null);
    if (daysLeft === null) return null;

    const urgent = daysLeft <= 3;
    return (
        <Link
            to={ROUTES.BILLING}
            data-testid="trial-header-chip"
            className={`mr-1 inline-flex h-7 items-center gap-1.5 rounded-full px-3 text-xs font-semibold whitespace-nowrap transition-colors ${
                urgent
                    ? 'bg-warning-100 text-warning-700 hover:bg-warning-200 dark:bg-warning-900/40 dark:text-warning-300'
                    : 'bg-primary/10 text-primary hover:bg-primary/15'
            }`}
        >
            <Clock aria-hidden="true" className="size-3.5" />
            {daysLeft === 0
                ? t('billing.trialChip.ended', 'Trial ended — choose a plan')
                : t('billing.trialChip.daysLeft', 'Trial · {{count}} days left', {
                      count: daysLeft,
                  })}
        </Link>
    );
}
